import { getCurrentConference } from '../base/conference/functions';
import { isMeetingSettingEnabled } from '../base/jwt/functions';
import { getLocalParticipant, getParticipantById, getRemoteParticipants, getScreenSharingParticipantId, isLocalParticipantModerator, isParticipantModerator, isParticipantPanelist, isParticipantPresenter, isScreenShareParticipant } from '../base/participants/functions';
import { toState } from '../base/redux/functions';
import { isPrejoinPageVisible } from '../prejoin/functions';
import { iAmVisitor } from '../visitors/functions';
import { BACKSTAGE_ROOM_SUFFIX, ENABLE_BACKSTAGE_MODE_SETTING, ENABLE_HIDDEN_ME_SETTING, WEBINAR_HIDDEN_DEFAULT_AUDIENCE } from './constants';
/**
 * One shared empty array for the roster selectors. A fresh literal would be a
 * new reference on every call, and useSelector compares by reference — the panes
 * reading these would re-render on every action dispatched in the meeting.
 */
const EMPTY_ROSTER = [];
/**
 * Whether the backstage feature set is switched on for this meeting, per the
 * token's {@code meeting.settings.ENABLE_BACKSTAGE_MODE}. On unless the
 * backend switches it off with {@code 0}: today's tokens do not carry the key
 * at all, and a moderator must not lose backstage over that. Client-side only
 * -- Prosody keeps enforcing "moderator only" regardless.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {boolean}
 */
export function isBackstageModeEnabled(stateful) {
    return isMeetingSettingEnabled(toState(stateful), ENABLE_BACKSTAGE_MODE_SETTING, true);
}
/**
 * Whether the hide-me feature set (hide yourself, join hidden, hide others) is
 * switched on for this meeting, per the token's
 * {@code meeting.settings.ENABLE_HIDDEN_ME}. Same rule as
 * {@link isBackstageModeEnabled}: on unless the backend says {@code 0}.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {boolean}
 */
export function isHiddenMeEnabled(stateful) {
    return isMeetingSettingEnabled(toState(stateful), ENABLE_HIDDEN_ME_SETTING, true);
}
/**
 * Whether a room name denotes a backstage room.
 *
 * @param {string} [name] - The room name.
 * @returns {boolean}
 */
export function isBackstageRoomName(name) {
    return Boolean(name && name.endsWith(BACKSTAGE_ROOM_SUFFIX));
}
/**
 * The name of the room the local participant is currently in, or undefined when
 * not in a conference.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {string|undefined}
 */
export function getCurrentRoomName(stateful) {
    var _a;
    return (_a = getCurrentConference(toState(stateful))) === null || _a === void 0 ? void 0 : _a.getName();
}
/**
 * Whether the local participant is currently in the backstage room (hidden from
 * the main meeting, able to Go Live).
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {boolean}
 */
export function amIInBackstage(stateful) {
    return isBackstageRoomName(getCurrentRoomName(stateful));
}
/**
 * The main meeting's room name, whether we are currently in it or in its
 * backstage room.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {string|undefined}
 */
export function getMainRoomName(stateful) {
    const name = getCurrentRoomName(stateful);
    if (!name) {
        return undefined;
    }
    return isBackstageRoomName(name) ? name.slice(0, -BACKSTAGE_ROOM_SUFFIX.length) : name;
}
/**
 * The backstage room's name for the current meeting.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {string|undefined}
 */
export function getBackstageRoomName(stateful) {
    const name = getCurrentRoomName(stateful);
    if (!name) {
        return undefined;
    }
    return isBackstageRoomName(name) ? name : name + BACKSTAGE_ROOM_SUFFIX;
}
/**
 * Returns the room-scoped audience layout. Stale values from another meeting
 * are ignored.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {IAudienceLayout}
 */
export function getAudienceLayout(stateful) {
    const state = toState(stateful);
    const webinar = state['features/webinar'];
    const currentRoom = getMainRoomName(state);
    const appliesToCurrentRoom = Boolean(currentRoom)
        && (webinar === null || webinar === void 0 ? void 0 : webinar.audienceLayoutRoom) === currentRoom;
    return {
        showOnlyPanelists: appliesToCurrentRoom && Boolean(webinar === null || webinar === void 0 ? void 0 : webinar.showOnlyPanelists),
        stageOnlyMode: appliesToCurrentRoom && Boolean(webinar === null || webinar === void 0 ? void 0 : webinar.stageOnlyMode)
    };
}
/**
 * Whether this local viewer should use a restricted webinar audience layout.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {boolean}
 */
export function isAudienceLayoutActive(stateful) {
    const state = toState(stateful);
    const { showOnlyPanelists, stageOnlyMode } = getAudienceLayout(state);
    return iAmVisitor(state) && (showOnlyPanelists || stageOnlyMode);
}
/**
 * Whether the local viewer's large video is restricted to the webinar stage.
 *
 * Show-only-panelists is deliberately excluded: it filters thumbnails but
 * leaves the active speaker visible on large video.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {boolean}
 */
export function isAudienceStageOnlyModeActive(stateful) {
    const state = toState(stateful);
    return iAmVisitor(state) && getAudienceLayout(state).stageOnlyMode;
}
/**
 * Returns the remote participants visible in the local audience layout.
 *
 * Stage-only mode prioritizes active screen shares and otherwise shows pinned
 * tiles. Show-only-panelists keeps the stage (moderators and panelists) and
 * drops ordinary participants; a screen share remains visible only when its
 * owner is on the stage.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {Array<Object>}
 */
export function getWebinarAudienceRemoteParticipants(stateful) {
    const state = toState(stateful);
    return _filterHiddenPresenters(state, _getAudienceLayoutParticipants(state));
}
/**
 * Whether a participant is currently sending a desktop (screen-share) track.
 * This fork uses the classic share model: the desktop track replaces the
 * camera track on the sender's own tile — there is no separate virtual
 * screen-share participant.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @param {string} participantId - The participant to check.
 * @returns {boolean}
 */
export function isParticipantScreenSharing(stateful, participantId) {
    if (!participantId) {
        return false;
    }
    const tracks = toState(stateful)['features/base/tracks'] || [];
    return tracks.some(t => t.participantId === participantId
        && (t.videoType === 'desktop' || t.mediaType === 'screenshare'));
}
/**
 * Whether a remote participant's tile is hidden from the local viewer.
 *
 * Two cases hide a tile from everyone who is not a moderator:
 * - presenters (stagehands): never visible — until they share, when their tile
 *   carries the shared content (classic share model, see
 *   isParticipantScreenSharing);
 * - occupants a moderator explicitly hid (webinarHidden presence property),
 *   including self-hidden moderators — same screen-share exemption.
 *
 * Moderators always see everyone so the flags can be managed.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @param {Object} participant - The participant to check.
 * @returns {boolean}
 */
export function isPresenterHiddenForLocal(stateful, participant) {
    const state = toState(stateful);
    const p = participant;
    if (!p || p.local) {
        return false;
    }
    if (p.webinarHidden === true) {
        // An explicitly hidden occupant carries the roles that may still see
        // them. Anything else — including a moderator, when a future audience
        // excludes them — does not.
        if (getHiddenAudience(p).includes(getLocalWebinarRole(state))) {
            return false;
        }
        return !isParticipantScreenSharing(state, p.id);
    }
    // Presenters are hidden from everyone who is not a moderator; that rule
    // predates the audience list and is not expressed through it.
    if (!isParticipantPresenter(p) || isLocalParticipantModerator(state)) {
        return false;
    }
    return !isParticipantScreenSharing(state, p.id);
}
/**
 * The roles that may still see a hidden participant.
 *
 * A participant hidden by a server that predates the audience property carries
 * no list; moderator-only is what that server meant, so it is the fallback.
 *
 * @param {Object} participant - The hidden participant.
 * @returns {Array<string>}
 */
export function getHiddenAudience(participant) {
    const raw = participant === null || participant === void 0 ? void 0 : participant.webinarHiddenVisibleTo;
    if (typeof raw !== 'string' || raw === '') {
        return [WEBINAR_HIDDEN_DEFAULT_AUDIENCE];
    }
    return raw.split(',').map(role => role.trim().toLowerCase())
        .filter(Boolean);
}
/**
 * The local viewer's role, in the vocabulary the hidden audience is written in.
 *
 * Moderator outranks the webinar roles, and a visitor is never any of them, so
 * the checks are ordered rather than combined.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {string}
 */
export function getLocalWebinarRole(stateful) {
    const state = toState(stateful);
    if (isLocalParticipantModerator(state)) {
        return 'moderator';
    }
    if (iAmVisitor(state)) {
        return 'visitor';
    }
    const local = getLocalParticipant(state);
    if (isParticipantPanelist(local)) {
        return 'panelist';
    }
    if (isParticipantPresenter(local)) {
        return 'presenter';
    }
    return 'participant';
}
/**
 * Drops hidden tiles (presenters, explicitly hidden occupants) for
 * non-moderator viewers. Screen-share tiles are deliberately untouched.
 *
 * @param {IStateful} state - The redux state.
 * @param {Array<Object>} participants - The pre-filtered participant list.
 * @returns {Array<Object>}
 */
function _filterHiddenPresenters(state, participants) {
    if (isLocalParticipantModerator(toState(state))) {
        return participants;
    }
    return participants.filter(participant => !isPresenterHiddenForLocal(state, participant));
}
/**
 * The audience-layout portion of {@link getWebinarAudienceRemoteParticipants}.
 *
 * @param {IStateful} state - The redux state.
 * @returns {Array<Object>}
 */
function _getAudienceLayoutParticipants(state) {
    var _a;
    if (!isAudienceLayoutActive(state)) {
        return getRemoteParticipants(state);
    }
    const { showOnlyPanelists, stageOnlyMode } = getAudienceLayout(state);
    const participants = state['features/base/participants'] || [];
    const remoteParticipants = participants.filter((participant) => !participant.local);
    // Both share models have to be recognized here. This fork normally uses the
    // classic one - the desktop track replaces the sharer's camera track on
    // their own tile, so there is no separate screen-share participant to find -
    // but a virtual screen-share participant still shows up on the paths that
    // create one. Matching only the latter is what left stage-only mode with an
    // empty stage whenever someone was actually sharing.
    const screenShares = remoteParticipants.filter((participant) => isScreenShareParticipant(participant) || isParticipantScreenSharing(state, participant.id));
    if (stageOnlyMode) {
        if (screenShares.length) {
            return screenShares;
        }
        const pinned = remoteParticipants.filter((participant) => !participant.fakeParticipant && (participant.moderatorPin || participant.pinned));
        if (pinned.length) {
            return pinned;
        }
        // Nothing was pinned or shared: keep the current stage occupant so
        // viewers are not left with a blank stage.
        const largeVideoId = (_a = state['features/large-video']) === null || _a === void 0 ? void 0 : _a.participantId;
        const eligible = remoteParticipants.filter((participant) => !participant.fakeParticipant && !isPresenterHiddenForLocal(state, participant));
        const stage = eligible.find((participant) => participant.id === largeVideoId);
        if (stage) {
            return [stage];
        }
        // Falling back to the large-video occupant alone deadlocks: the large
        // video is elected FROM this list (see _electParticipantInLargeVideo),
        // so before anything is on stage the list is empty, nothing can be
        // elected, and it stays empty. Offer the dominant speaker - or any
        // eligible occupant - so the stage can be seeded.
        const speaker = eligible.find((participant) => participant.dominantSpeaker);
        return speaker ? [speaker] : eligible.slice(0, 1);
    }
    if (showOnlyPanelists) {
        return remoteParticipants.filter((participant) => {
            // Moderators are stage occupants, not audience: hiding them left
            // visitors with an empty grid in every meeting where nobody had been
            // granted the panelist role explicitly - which is the common case,
            // since the host is a moderator. _filterHiddenPresenters still drops
            // any moderator who hid themselves.
            if (isParticipantModerator(participant) || isParticipantPanelist(participant)) {
                return true;
            }
            // A presenter's own tile is normally hidden, but with the classic
            // share model their tile IS the shared content while they share.
            if (isParticipantPresenter(participant)) {
                return isParticipantScreenSharing(state, participant.id);
            }
            if (!isScreenShareParticipant(participant)) {
                return false;
            }
            const owner = getParticipantById(state, getScreenSharingParticipantId(participant.id));
            return isStageParticipant(owner) || isParticipantPresenter(owner);
        });
    }
    return getRemoteParticipants(state);
}
/**
 * Returns participants eligible for the viewer's large video. The large video
 * is restricted only by stage-only mode; show-only-panelists affects the
 * filmstrip alone.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {Array<Object>}
 */
export function getWebinarAudienceLargeVideoParticipants(stateful) {
    const state = toState(stateful);
    return isAudienceStageOnlyModeActive(state)
        ? getWebinarAudienceRemoteParticipants(state)
        : state['features/base/participants'] || [];
}
/**
 * Whether local recording is allowed in the current room per the moderator-set
 * policy (room metadata {@code localRecording.allowed}). Absent policy means
 * allowed — the pre-policy behavior.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {boolean}
 */
export function isLocalRecordingAllowedInRoom(stateful) {
    var _a;
    return ((_a = toState(stateful)['features/webinar']) === null || _a === void 0 ? void 0 : _a.localRecordingAllowed) !== false;
}
/**
 * Whether the local participant may record the meeting. A participant in the
 * backstage room follows the main room policy pushed by Prosody; everywhere
 * else the current room metadata is authoritative. Missing policy remains
 * allowed for backwards compatibility.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {boolean}
 */
export function isLocalRecordingAllowedForLocalParticipant(stateful) {
    var _a, _b;
    const state = toState(stateful);
    if (amIInBackstage(state)) {
        return ((_b = (_a = state['features/webinar']) === null || _a === void 0 ? void 0 : _a.mainRoomPolicy) === null || _b === void 0 ? void 0 : _b.localRecordingAllowed) !== false;
    }
    return isLocalRecordingAllowedInRoom(state);
}
/**
 * The main room's room-wide policies as last pushed by Prosody, for the
 * switches a backstage moderator sees. Backstage is its own room, so the
 * local conference's av-moderation state and metadata describe the backstage
 * room, not the one the audience is in. Undefined until the first roster push.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {Object|undefined}
 */
export function getMainRoomPolicy(stateful) {
    var _a;
    return (_a = toState(stateful)['features/webinar']) === null || _a === void 0 ? void 0 : _a.mainRoomPolicy;
}
/**
 * Whether "block everyone's mic and camera" is on in the current room, as
 * read from room metadata.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {boolean}
 */
export function isMediaBlockAllEnabled(stateful) {
    var _a;
    return ((_a = toState(stateful)['features/webinar']) === null || _a === void 0 ? void 0 : _a.mediaBlockAll) === true;
}
/**
 * Whether the local participant may not send the given media because the room
 * has "block everyone's mic and camera" on. Moderators are exempt; nobody else
 * is -- a panelist or presenter is blocked like anyone else, and a visitor who
 * gets promoted arrives blocked.
 *
 * This is a floor under the per-participant lock (lockAudio / lockVideo): that
 * lock can add restrictions on top, it cannot lift this one.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {boolean}
 */
export function isMediaBlockedForLocal(stateful) {
    const state = toState(stateful);
    return isMediaBlockAllEnabled(state) && !isLocalParticipantModerator(state);
}
/**
 * Whether a participant holds a stage role (moderator or panelist) and so
 * belongs to the backstage/Go Live flow at all.
 *
 * @param {Object} participant - The participant object.
 * @returns {boolean}
 */
export function isStageParticipant(participant) {
    return isParticipantModerator(participant) || isParticipantPanelist(participant);
}
/**
 * Whether the local participant holds a stage role.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {boolean}
 */
export function amIStageParticipant(stateful) {
    return isStageParticipant(getLocalParticipant(toState(stateful)));
}
/**
 * Whether the prejoin "start in backstage" choice should now be applied.
 *
 * This must not fire while the prejoin screen is still up: this fork can hold a
 * live conference during prejoin (it shows the participant count), and switching
 * rooms mid-prejoin both breaks the switch handshake and gets undone by the
 * actual join. It also requires the local user to be a confirmed moderator; a
 * JWT moderator's role can be set before the join completes.
 *
 * This is the FALLBACK path (join main, then switch): token moderators are
 * direct-joined into backstage by the prejoin middleware instead (see
 * shouldDirectJoinBackstage), which consumes the flag, so this listener only
 * fires for cases where the direct join could not be taken.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {boolean}
 */
export function shouldApplyStartBackstage(stateful) {
    var _a;
    const state = toState(stateful);
    return Boolean((_a = state['features/webinar']) === null || _a === void 0 ? void 0 : _a.startBackstage)
        && isBackstageModeEnabled(state)
        && !isPrejoinPageVisible(state)
        && Boolean(getCurrentConference(state))
        && !amIInBackstage(state)
        && isLocalParticipantModerator(state);
}
/**
 * Whether the prejoin join should target the backstage room directly instead of
 * joining the main room and switching afterwards.
 *
 * The join-then-switch fallback makes the moderator flash in the main room for
 * a few seconds — every guest and visitor sees them join and immediately leave.
 * When the "start in backstage" choice is made by a token moderator we already
 * know the outcome before the join, so the initial MUC join can go straight to
 * '<room>-backstage' and the main room never sees any presence.
 *
 * Token moderators only: for anonymous users moderator-ship is granted after
 * the main-room join, so a direct backstage join cannot be authorized up front.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {boolean}
 */
export function shouldDirectJoinBackstage(stateful) {
    var _a, _b;
    const state = toState(stateful);
    const jwt = state['features/base/jwt'];
    return Boolean((_a = state['features/webinar']) === null || _a === void 0 ? void 0 : _a.startBackstage)
        && isBackstageModeEnabled(state)
        && Boolean(((_b = jwt === null || jwt === void 0 ? void 0 : jwt.user) === null || _b === void 0 ? void 0 : _b.moderator) || (jwt === null || jwt === void 0 ? void 0 : jwt.moderator));
}
/**
 * Whether the local participant is backstage without a stage role: sent there
 * by a moderator, and kept there by the server until a moderator brings them
 * live. The Go Live control is not theirs to use.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {boolean}
 */
export function amIHeldBackstage(stateful) {
    const state = toState(stateful);
    return amIInBackstage(state) && !amIStageParticipant(state) && !amIWebinarModerator(state);
}
/**
 * The people currently in the backstage room, as published by the server to
 * main-room moderators. Empty for everyone else, and while backstage (the
 * roster describes the room you are not in).
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {Array<Object>}
 */
export function getBackstageRoster(stateful) {
    var _a, _b;
    const state = toState(stateful);
    if (!isLocalParticipantModerator(state) || amIInBackstage(state)) {
        return EMPTY_ROSTER;
    }
    return (_b = (_a = state['features/webinar']) === null || _a === void 0 ? void 0 : _a.backstageRoster) !== null && _b !== void 0 ? _b : EMPTY_ROSTER;
}
/**
 * The people currently in the main room, as published by the server to
 * moderators who are sitting backstage. Empty for everyone else, and while in
 * the main room (the roster describes the room you are not in).
 *
 * Deliberately not gated on the local moderator role: the server only ever
 * sends this to a moderator, and in backstage the local role can understate
 * that (see {@link amIWebinarModerator}). Gating on it here would hide the
 * pane's Main room tab from exactly the people the server went out of its way
 * to keep serving.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {Array<Object>}
 */
export function getMainRoomRoster(stateful) {
    var _a, _b;
    const state = toState(stateful);
    if (!amIInBackstage(state)) {
        return EMPTY_ROSTER;
    }
    return (_b = (_a = state['features/webinar']) === null || _a === void 0 ? void 0 : _a.mainRoomRoster) !== null && _b !== void 0 ? _b : EMPTY_ROSTER;
}
/**
 * Whether the local participant may act as a moderator of this meeting.
 *
 * Three sources, because no single one covers the backstage room. The MUC role
 * is the normal answer. The signed token claim covers the moment before the role
 * is settled. And the main-room roster covers the case the other two miss: in
 * backstage mod_token_moderation only crowns token moderators, so someone who
 * was granted moderator at runtime arrives there reading as a plain participant
 * — but the server still treats them as one (see is_trusted_moderator in
 * mod_webinar_roles), and that roster is something it only ever sends to a
 * moderator. Receiving it is itself the proof.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {boolean}
 */
export function amIWebinarModerator(stateful) {
    var _a;
    const state = toState(stateful);
    const jwt = state['features/base/jwt'];
    return isLocalParticipantModerator(state)
        || Boolean(((_a = jwt === null || jwt === void 0 ? void 0 : jwt.user) === null || _a === void 0 ? void 0 : _a.moderator) || (jwt === null || jwt === void 0 ? void 0 : jwt.moderator))
        || getMainRoomRoster(state).length > 0;
}
/**
 * Whether the local participant may ask to join hidden.
 *
 * Offered exactly when the request would be honoured: prosody accepts it only
 * from an occupant that is a moderator by the time the join presence is
 * processed, which on prejoin is decided by the token's moderator claim.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {boolean}
 */
export function canJoinHidden(stateful) {
    var _a;
    const state = toState(stateful);
    if (!isHiddenMeEnabled(state)) {
        return false;
    }
    // Already in the meeting: the real MUC role is what the server will check.
    if (isLocalParticipantModerator(state)) {
        return true;
    }
    // On prejoin there is no MUC role yet. 'none' is what the jwt middleware
    // stores for a token with no moderator claim.
    const { localUserRole } = (_a = state['features/base/jwt']) !== null && _a !== void 0 ? _a : {};
    return Boolean(localUserRole) && localUserRole !== 'none';
}
/**
 * The audience recorded for a prejoin "join hidden" choice, or undefined when
 * the local participant chose to join normally.
 *
 * @param {IStateful} stateful - The redux store or state.
 * @returns {string|undefined}
 */
export function getJoinHiddenAudience(stateful) {
    var _a;
    return (_a = toState(stateful)['features/webinar']) === null || _a === void 0 ? void 0 : _a.joinHiddenAudience;
}
